import { processFitWiseSizeList, processMeasurementData } from '@store/actions/MeasurementAction';
import { Types } from '@store/types';

import { insertAt } from '@utils/Util';

const initialState = {
	sizeCategories: [],
	fitWiseSizeList: {},
	initialMeasurementTableData: [],
	measurementTable: [],
	changeLog: [],
	historyInfo: {},
	sizeCategorySequence: {},
	hasShowActualAndDiffColumn: false,
	initId: null,
	isShowBlockUsedFrom: false,
	hiddenColumnIds: []
};

const MeasurementReducer = (state = initialState, action) => {
	switch (action.type) {
		case Types.FETCH_ALL_MEASUREMENT_CATEGORIES:
			return { ...state, sizeCategories: action.payload };
		case Types.FETCH_PRODUCT_MEASUREMENT_TABLE_DATA:
			return {
				...state,
				measurementTable: processMeasurementData(action.payload),
				initialMeasurementTableData: action.payload,
				fitWiseSizeList: processFitWiseSizeList(action.payload),
				changeLog: []
			};
		case Types.ADD_MEASUREMENT_SIZE_CATEGORY_SEQUENCE:
			return {
				...state,
				sizeCategorySequence: action.payload
			};
		case Types.ADD_PRODUCT_MEASUREMENT_CHANGE_LOG:
			return {
				...state,
				changeLog: [...state.changeLog, action.payload]
			};
		case Types.CLEAR_MEASUREMENT_CHANGE_LOG:
			return {
				...state,
				changeLog: []
			};
		case Types.ADD_PRODUCT_MEASUREMENT_BULK_CHANGE_LOG:
			return {
				...state,
				changeLog: [...state.changeLog, ...action.payload]
			};
		case Types.ADD_NEW_POINT_IN_MEASUREMENT_TABLE:
			return {
				...state,
				measurementTable: [...state.measurementTable, action.payload]
			};
		case Types.UPDATE_MEASUREMENT_TABLE:
			return {
				...state,
				measurementTable: [...action.payload]
			};
		case Types.REPLACE_POM_IN_MEASUREMENT_TABLE:
			return {
				...state,
				changeLog: [...state.changeLog, action.payload]
			};
		case Types.REMOVE_POINT_IN_MEASUREMENT_TABLE:
			return {
				...state,
				measurementTable: state.measurementTable?.filter(
					(item) => item.pomResponse.id !== action.payload
				)
			};
		case Types.REMOVE_LIBRARY_POINT_IN_MEASUREMENT_TABLE:
			return {
				...state,
				measurementTable: state.measurementTable?.filter(
					(item) =>
						item.pomResponse.libraryPOMId !== action.payload &&
						item.pomResponse.id !== action.payload
				)
			};
		case Types.ADD_NEW_SIZE_IN_MEASUREMENT_TABLE: {
			return {
				...state,
				fitWiseSizeList: action.payload.fitWiseSizeList,
				measurementTable: state.measurementTable?.map((item) => {
					for (let fit in item.fitInfo) {
						if (action.payload.isNeedToMerge) {
							item.fitInfo[fit].sizeValueList = JSON.parse(
								JSON.stringify([
									...item.fitInfo[fit].sizeValueList,
									...action.payload.sizeValueList
								])
							);
						} else {
							let valueList = JSON.parse(JSON.stringify(action.payload.sizeValueList));
							item.fitInfo[fit].sizeValueList = valueList?.map((obj) => ({
								...obj,
								value: item.fitInfo[fit].sizeValueList?.[0]?.value || ''
							}));
						}
					}
					return item;
				})
			};
		}
		case Types.MODIFY_MEASUREMENT_TABLE_POINT_IDS:
			return {
				...state,
				measurementTable: state.measurementTable?.map((item) => {
					if (item.pomResponse.id === action.payload.pointId) {
						item.pomResponse.id = action.payload.value;
						item.pomResponse.libraryPOMId = action.payload.value;
						item.pomResponse.referenceId = action.payload.referenceId;
						item.fitInfo.FIRST_FIT.pomResponseId = action.payload.value;
					}
					return item;
				})
			};
		case Types.MODIFY_MEASUREMENT_TABLE_POINT_CODE:
			return {
				...state,
				measurementTable: state.measurementTable?.map((item) => {
					if (item.pomResponse.id === action.payload.pointId) {
						item.pomResponse.pomCode = action.payload.value;
					}
					return item;
				})
			};
		case Types.MODIFY_MEASUREMENT_TABLE_POINT_NAME:
			return {
				...state,
				measurementTable: state.measurementTable?.map((item) => {
					if (item.pomResponse.id === action.payload.pointId) {
						item.pomResponse.name = action.payload.value;
					}
					return item;
				})
			};
		case Types.MODIFY_MEASUREMENT_TABLE_CELL_VALUE: {
			let { pointId, fitType, fieldName, value } = action.payload;
			return {
				...state,
				measurementTable: state.measurementTable?.map((item) => {
					if (item.pomResponse.id === pointId) {
						item.fitInfo[fitType] = {
							...item.fitInfo[fitType],
							[fieldName]: value
						};
					}
					return item;
				})
			};
		}
		case Types.REARRANGE_MEASUREMENT_TABLE_ROW: {
			let { sourceIndex, destinationIndex, fitId } = action.payload;
			let cloneTableData = [...state.measurementTable];
			let sourceIndexData = cloneTableData[sourceIndex];
			cloneTableData.splice(sourceIndex, 1);
			insertAt(cloneTableData, destinationIndex, sourceIndexData);

			return {
				...state,
				measurementTable: cloneTableData,
				changeLog: [
					...state.changeLog,
					{
						changeLogType: 'PRODUCT_MEASUREMENT_ORDERING_UPDATE',
						id: fitId || null,
						orderingRequests: cloneTableData?.map((item, index) => ({
							pointOfMeasurementId: item.pomResponse.id,
							sl: index
						}))
					}
				]
			};
		}

		case Types.MODIFY_MEASUREMENT_TABLE_BULK_SIZE_CELL_VALUE: {
			let { pointId, fitType, modifiedSizeList } = action.payload;
			return {
				...state,
				measurementTable: state.measurementTable?.map((item) => {
					if (item.pomResponse.id === pointId) {
						item.fitInfo[fitType] = {
							...item.fitInfo[fitType],
							sizeValueList: item.fitInfo[fitType].sizeValueList?.map((size) => {
								let selectedSize = modifiedSizeList.find((newSize) => newSize.id === size.id);
								if (selectedSize?.id) {
									size.value = selectedSize.value;
								}
								return size;
							})
						};
					}
					return item;
				})
			};
		}
		case Types.ADD_MEASUREMENT_GROUP_GRADING: {
			const { groupGradingBaseSize } = action.payload;
			return {
				...state,
				fitWiseSizeList: {
					FIRST_FIT: { ...state.fitWiseSizeList['FIRST_FIT'], groupGradingBaseSize }
				}
			};
		}
		case Types.REMOVE_MEASUREMENT_GROUP_GRADING: {
			return {
				...state,
				measurementTable: state.measurementTable?.map((item) => {
					item.fitInfo['FIRST_FIT'] = {
						...item.fitInfo['FIRST_FIT']
						// grading3: undefined
					};
					return item;
				}),
				fitWiseSizeList: {
					FIRST_FIT: { ...state.fitWiseSizeList['FIRST_FIT'], groupGradingBaseSize: undefined }
				}
			};
		}
		case Types.MODIFY_MEASUREMENT_BASE_SIZE: {
			const { baseSize } = action.payload;
			return {
				...state,
				measurementTable: state.measurementTable?.map((item) => {
					item.fitInfo['FIRST_FIT'] = {
						...item.fitInfo['FIRST_FIT'],
						baseSize
					};
					return item;
				}),
				fitWiseSizeList: {
					FIRST_FIT: { ...state.fitWiseSizeList['FIRST_FIT'], baseSize }
				}
			};
		}
		case Types.REMOVE_MEASUREMENT_SIZE: {
			const { sizeId } = action.payload;
			let sizeList = state.fitWiseSizeList['FIRST_FIT'];
			return {
				...state,
				measurementTable: state.measurementTable?.map((item) => {
					let info = item.fitInfo['FIRST_FIT'];
					item.fitInfo['FIRST_FIT'] = {
						...info,
						baseSize: info.baseSize === sizeId ? undefined : info.baseSize,
						sizeValueList: info.sizeValueList?.filter((item) => item.id !== sizeId)
					};
					return item;
				}),
				fitWiseSizeList: {
					FIRST_FIT: {
						...sizeList,
						baseSize: sizeList.baseSize === sizeId ? undefined : sizeList.baseSize,
						sizeList: sizeList.sizeList?.filter((item) => item.id !== sizeId)
					}
				}
			};
		}
		case Types.PASTE_MEASUREMENT_SIZE_VALUES: {
			const { sizeId, modifiedSizeList, currentFit } = action.payload;
			return {
				...state,
				measurementTable: state.measurementTable?.map((item) => {
					let info = item.fitInfo[currentFit];
					if ([info.pomResponseId] in modifiedSizeList) {
						item.fitInfo[currentFit] = {
							...info,
							sizeValueList: info.sizeValueList?.map((item) => {
								if (item.id === sizeId) {
									item.value = modifiedSizeList[info.pomResponseId];
								}
								return item;
							})
						};
					}
					return item;
				})
			};
		}
		case Types.CLEAR_MEASUREMENT_TABLE: {
			return {
				sizeCategories: [],
				fitWiseSizeList: {},
				initialMeasurementTableData: [],
				measurementTable: [],
				changeLog: [],
				historyInfo: {},
				initId: null
			};
		}
		case Types.SET_MEASUREMENT_HISTORY_INFO: {
			return {
				...state,
				historyInfo: action.payload,
				changeLog: []
			};
		}
		case Types.SET_MEASUREMENT_SHOW_COLUMN_VALUE:
			return {
				...state,
				hasShowActualAndDiffColumn: action.payload
			};
		case Types.ADD_INIT_MEASUREMENT_ID:
			return {
				...state,
				initId: action.payload
			};
		case Types.ADD_CHANGE_LOG_ON_DUPLICATE:
			return {
				...state,
				changeLog: action.payload
			};
		case Types.CONVERT_MEASUREMENT_SIZE_TO:
			return {
				...state,
				fitWiseSizeList: action.payload
			};
		case Types.CONVERT_MEASUREMENT_SIZE_VALUE:
			return {
				...state,
				measurementTable: action.payload
			};
		case Types.CONVERT_MEASUREMENT_SIZE_CHANGE_LOG:
			return {
				...state,
				changeLog: [...state.changeLog, ...action.payload]
			};
		case Types.IS_SHOW_BLOCK_USED_FROM:
			return {
				...state,
				isShowBlockUsedFrom: action.payload
			};
		case Types.HIDE_MEASUREMENT_TABLE_COLUMNS: {
			const uniqueHiddenColumnIds = new Set(state.hiddenColumnIds);
			action.payload &&
				action.payload.length &&
				action.payload.forEach((columnId) => {
					uniqueHiddenColumnIds.add(columnId);
				});
			const updatedHiddenColumnIds = [...uniqueHiddenColumnIds];
			return {
				...state,
				hiddenColumnIds: updatedHiddenColumnIds
			};
		}
		case Types.SHOW_ALL_MEASUREMENT_TABLE_COLUMN:
			return {
				...state,
				hiddenColumnIds: []
			};
		default:
			return state;
	}
};

export default MeasurementReducer;
